import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import "./CcUnit.css";

import Introduce from "./components/CcIntroduce";
import Template from "./components/Template/Template";
import CcUnitTemplateDetail from "./components/Template/TemplateDetail";

import { publicApiClient } from "../../../api/apiClient";

const TAB = {
  INTRODUCE: "introduce",
  TEMPLATE: "template",
};

const COLLEGE_META = [
  {
    path: "Scitech",
    collegeId: 1,
    name: "과학기술대학 학생회",
    shortName: "과학기술대학",
  },
  {
    path: "Global",
    collegeId: 2,
    name: "글로벌융합대학 학생회",
    shortName: "글로벌융합대학",
  },
  {
    path: "Future",
    collegeId: 3,
    name: "미래인재대학 학생회",
    shortName: "미래인재대학",
  },
  {
    path: "Pharm",
    collegeId: 4,
    name: "약학대학 학생회",
    shortName: "약학대학",
  },
  {
    path: "ArtDesign",
    collegeId: 5,
    name: "Art & Design 대학 학생회",
    shortName: "Art & Design 대학",
  },
  {
    path: "Club",
    collegeId: 6,
    name: "동아리연합회",
    shortName: "동아리연합회",
  },
];

const COUNCIL_NAMES_STORAGE_KEY = "councilNames";

const getSavedCouncilNames = () => {
  try {
    const saved = localStorage.getItem(COUNCIL_NAMES_STORAGE_KEY);

    if (!saved) return [];

    const parsed = JSON.parse(saved);

    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const applySavedCouncilNames = (collegeList, savedNames) => {
  return collegeList.map((college) => {
    const saved = savedNames.find(
      (item) => Number(item.collegeId || item.id) === Number(college.collegeId)
    );

    if (!saved) return college;

    const savedName = saved.currentName || saved.name || college.name;
    const savedShortName =
      saved.shortName || savedName.replace(" 학생회", "") || college.shortName;

    return {
      ...college,
      name: savedName,
      shortName: savedShortName,
      logoUrl: saved.logoUrl || college.logoUrl || "",
    };
  });
};

const normalizeActiveTab = (value) => {
  if (value === TAB.TEMPLATE) return TAB.TEMPLATE;
  if (value === "intro" || value === TAB.INTRODUCE) return TAB.INTRODUCE;

  return TAB.INTRODUCE;
};

const normalizeDate = (value) => {
  if (!value) return "";

  if (typeof value === "string") {
    return value.slice(0, 10);
  }

  return "";
};

const normalizeCategory = (value) => {
  if (!value) return "기타";

  const categoryMap = {
    ALL: "전체",
    BUDGET: "회계",
    EVENT: "행사/사업",
    ADMINISTRATION: "행정/운영",
    ETC: "기타",

    RULE: "회칙",
    BYLAW: "세칙",
    FORM: "양식",
    DATA: "자료",
    MINUTES: "회의록",
    MEETING: "회의록",
  };

  return categoryMap[value] || value;
};

const getDataFromApiResponse = (result) => {
  return result?.data?.data || result?.data || result?.result || result;
};

const normalizeFileItem = (file) => {
  if (!file || typeof file !== "object") return null;

  return {
    id:
      file.formFileId ||
      file.collegeFormFileId ||
      file.collegeFormsFileId ||
      file.fileId ||
      file.id ||
      null,
    name:
      file.name ||
      file.originalName ||
      file.originalFileName ||
      file.fileName ||
      file.savedName ||
      "첨부파일",
    size: file.size || file.fileSize || "",
    url: file.url || file.fileUrl || file.downloadUrl || file.filePath || "",
  };
};

const normalizeFiles = (item) => {
  if (!item) return [];

  const fileList = [];

  const possibleArrays = [
    item.files,
    item.fileList,
    item.formFiles,
    item.formFileList,
    item.collegeFormFiles,
    item.collegeFormsFiles,
    item.fileResponses,
    item.formFileResponses,
    item.attachments,
  ];

  possibleArrays.forEach((array) => {
    if (Array.isArray(array)) {
      fileList.push(...array);
    }
  });

  const possibleSingleFiles = [
    item.file,
    item.attachment,
    item.attachFile,
    item.formFile,
    item.fileResponse,
    item.formFileResponse,
  ];

  possibleSingleFiles.forEach((fileItem) => {
    if (fileItem && typeof fileItem === "object") {
      fileList.push(fileItem);
    }
  });

  const normalizedFiles = fileList.map(normalizeFileItem).filter(Boolean);

  return normalizedFiles.filter((file, index, array) => {
    const key = file.id || file.url || file.name;

    return (
      array.findIndex((target) => {
        const targetKey = target.id || target.url || target.name;
        return targetKey === key;
      }) === index
    );
  });
};

const getListFromPagedResponse = (result) => {
  const data = getDataFromApiResponse(result);

  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.content)) return data.content;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.list)) return data.list;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.data?.content)) return data.data.content;

  return [];
};

const normalizePost = (item) => {
  if (!item) return null;

  const files = normalizeFiles(item);

  return {
    id:
      item.collegeFormsId ||
      item.collegeFormId ||
      item.formId ||
      item.id ||
      item.postId,
    category: normalizeCategory(
      item.category || item.listCategory || item.formListCategory
    ),
    rawCategory:
      item.category || item.listCategory || item.formListCategory || "",
    title: item.title || "",
    writer: item.name || "관리자",
    date: normalizeDate(
      item.createdAt || item.createdDate || item.date || item.updatedAt
    ),
    view: item.viewer ?? item.view ?? item.viewCount ?? item.views ?? 0,
    content: item.content || item.body || "",
    files,
    file: files[0] || null,
  };
};

export default function CcUnit() {
  const location = useLocation();
  const navigate = useNavigate();
  const { unit, id } = useParams();

  const [activeTab, setActiveTab] = useState(() =>
    normalizeActiveTab(location.state?.activeTab)
  );

  const [profileData, setProfileData] = useState(null);
  const [isProfileLoading, setIsProfileLoading] = useState(false);

  const [apiPosts, setApiPosts] = useState([]);
  const [isPostsLoading, setIsPostsLoading] = useState(false);

  const [savedCouncilNames, setSavedCouncilNames] = useState(
    getSavedCouncilNames
  );

  const [category, setCategory] = useState("ALL");
  const [search, setSearch] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchType, setSearchType] = useState("TITLE");
  const [page, setPage] = useState(1);

  useEffect(() => {
    const syncCouncilNames = () => {
      setSavedCouncilNames(getSavedCouncilNames());
    };

    window.addEventListener("storage", syncCouncilNames);
    window.addEventListener("councilNamesUpdated", syncCouncilNames);

    return () => {
      window.removeEventListener("storage", syncCouncilNames);
      window.removeEventListener("councilNamesUpdated", syncCouncilNames);
    };
  }, []);

  const collegeMeta = useMemo(() => {
    return applySavedCouncilNames(COLLEGE_META, savedCouncilNames);
  }, [savedCouncilNames]);

  const currentCouncil = useMemo(() => {
    return collegeMeta.find((item) => item.path === unit);
  }, [collegeMeta, unit]);

  const collegeId = currentCouncil?.collegeId;

  const handleTabClick = (tab) => {
    setActiveTab(tab);

    navigate(`/cc/${unit}`, {
      state: { activeTab: tab },
    });
  };

  const handleSearch = () => {
    setSearchQuery(search);
    setPage(1);
  };

  useEffect(() => {
    setPage(1);
  }, [category, searchType]);

  useEffect(() => {
    if (id) {
      setActiveTab(TAB.TEMPLATE);
      return;
    }

    setActiveTab(normalizeActiveTab(location.state?.activeTab));
  }, [unit, id, location.state]);

  useEffect(() => {
    const fetchCollegeProfile = async () => {
      if (!collegeId) {
        setProfileData(null);
        return;
      }

      try {
        setIsProfileLoading(true);

        const result = await publicApiClient(
          `/api/colleges/${collegeId}/profile`,
          {
            method: "GET",
          }
        );

        const data = getDataFromApiResponse(result);

        if (!data) {
          setProfileData(null);
          return;
        }

        setProfileData({
          type: data.type || "STUDENT_COUNCIL",
          name: data.name || "",
          slogan: data.slogan || "",
          profileImg: data.profileImg || "",
          officeLocation: data.officeLocation || "",
          kakaoUrl: data.kakaoUrl || "",
          instagramUrl: data.instagramUrl || "",
        });
      } catch (error) {
        console.error("중운위 소개 조회 실패:", error);
        setProfileData(null);
      } finally {
        setIsProfileLoading(false);
      }
    };

    fetchCollegeProfile();
  }, [collegeId]);

  useEffect(() => {
    const fetchCollegeForms = async () => {
      if (!collegeId) {
        setApiPosts([]);
        return;
      }

      try {
        setIsPostsLoading(true);

        const params = new URLSearchParams({
          listCategory: category,
          keyWordCategory: searchType,
          page: "0",
          size: "100",
        });

        if (searchQuery.trim()) {
          params.append("keyWord", searchQuery.trim());
        }

        const result = await publicApiClient(
          `/api/colleges/${collegeId}/form?${params.toString()}`,
          {
            method: "GET",
          }
        );

        const list = getListFromPagedResponse(result);

        setApiPosts(
          list
            .map((item) => normalizePost(item))
            .filter((item) => item && item.id)
        );
      } catch (error) {
        console.error("중운위 게시판 목록 조회 실패:", error);
        setApiPosts([]);
      } finally {
        setIsPostsLoading(false);
      }
    };

    fetchCollegeForms();
  }, [collegeId, category, searchType, searchQuery]);

  return (
    <>
      <div className="cc-unit-header">
        <h2 className="cc-unit-title">{currentCouncil?.shortName}</h2>

        <div className="cc-unit-tabs">
          <button
            type="button"
            className={activeTab === TAB.INTRODUCE ? "active" : ""}
            onClick={() => handleTabClick(TAB.INTRODUCE)}
          >
            학생회 소개
          </button>

          <button
            type="button"
            className={activeTab === TAB.TEMPLATE ? "active" : ""}
            onClick={() => handleTabClick(TAB.TEMPLATE)}
          >
            게시판
          </button>
        </div>
      </div>

      {id ? (
        <CcUnitTemplateDetail
          council={currentCouncil}
          unit={unit}
          collegeId={collegeId}
          posts={apiPosts}
        />
      ) : (
        <>
          {activeTab === TAB.INTRODUCE && (
            <>
              {isProfileLoading ? (
                <div className="cc-unit-info">소개 정보를 불러오는 중입니다.</div>
              ) : (
                <Introduce data={profileData} council={currentCouncil} />
              )}
            </>
          )}

          {activeTab === TAB.TEMPLATE && (
            <>
              {isPostsLoading ? (
                <div className="cc-unit-template">
                  <div className="cc-unit-template-inner">
                    게시글을 불러오는 중입니다.
                  </div>
                </div>
              ) : (
                <Template
                  data={apiPosts}
                  council={currentCouncil}
                  unit={unit}
                  category={category}
                  setCategory={setCategory}
                  search={search}
                  setSearch={setSearch}
                  searchType={searchType}
                  setSearchType={setSearchType}
                  handleSearch={handleSearch}
                  page={page}
                  setPage={setPage}
                />
              )}
            </>
          )}
        </>
      )}
    </>
  );
}